import {
  AiDocumentReviewAllowedActions,
  AiDocumentReviewMode,
} from 'src/constants/ai-document-review/enums'
import type { CollectDataStepType } from 'src/constants/collect-data/enums'
import type {
  AiDocumentReviewConfig,
  AiDocumentReviewConfigCreatePayload,
  AiDocumentReviewConfigFormValues,
  AiDocumentReviewConfigUpdatePayload,
} from 'src/interfaces/aiDocumentReviewConfig'
import {
  AI_REVIEW_CONFIG_INITIAL_VALUES,
  AI_REVIEW_DEFAULT_AUTO_APPROVE_MIN_SLP_RATE,
} from 'src/constants/ai-review/constants'

export function buildFormValues(
  config: AiDocumentReviewConfig | null,
): AiDocumentReviewConfigFormValues {
  if (!config) {
    return AI_REVIEW_CONFIG_INITIAL_VALUES
  }

  const hasThreshold =
    config.autoApproveMinSlpRate !== undefined && config.autoApproveMinSlpRate !== null

  return {
    stepType: config.stepType,
    label: config.label ?? '',
    enabled: config.enabled !== false,
    mode: config.mode ?? AiDocumentReviewMode.Shadow,
    allowedActions: config.allowedActions ?? AiDocumentReviewAllowedActions.All,
    instructions: config.instructions ?? '',
    useInternalReviewInstructions: config.useInternalReviewInstructions !== false,
    hasAutoApproveMinSlpRate: hasThreshold,
    autoApproveMinSlpRate: hasThreshold
      ? Number(config.autoApproveMinSlpRate)
      : AI_REVIEW_DEFAULT_AUTO_APPROVE_MIN_SLP_RATE,
    identityMatchRequired: config.identityMatchRequired !== false,
    vehicleMatchRequired: config.vehicleMatchRequired !== false,
    expirationCheckRequired: config.expirationCheckRequired !== false,
  }
}

function buildSharedFields(values: AiDocumentReviewConfigFormValues) {
  return {
    enabled: values.enabled,
    mode: values.mode,
    allowedActions: values.allowedActions,
    useInternalReviewInstructions: values.useInternalReviewInstructions,
    autoApproveMinSlpRate: values.hasAutoApproveMinSlpRate
      ? Math.round(values.autoApproveMinSlpRate)
      : undefined,
    identityMatchRequired: values.identityMatchRequired,
    vehicleMatchRequired: values.vehicleMatchRequired,
    expirationCheckRequired: values.expirationCheckRequired,
  }
}

export function buildCreatePayload(
  partnerId: string,
  values: AiDocumentReviewConfigFormValues,
): AiDocumentReviewConfigCreatePayload {
  const label = values.label.trim()
  const instructions = values.instructions.trim()

  return {
    partnerId,
    stepType: values.stepType as CollectDataStepType,
    label: label === '' ? undefined : label,
    instructions: instructions === '' ? undefined : instructions,
    ...buildSharedFields(values),
  }
}

/**
 * `instructions` is always sent so clearing the textarea clears the stored text. The threshold is
 * only sent when set — the update endpoint takes an integer and has no way to express "unset".
 */
export function buildUpdatePayload(
  values: AiDocumentReviewConfigFormValues,
): AiDocumentReviewConfigUpdatePayload {
  return {
    instructions: values.instructions.trim(),
    ...buildSharedFields(values),
  }
}
